import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Icon } from './Icon';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(1.875rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-3.125rem);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(3.125rem);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const HeroSection = styled.section`
  position: relative;
  padding: 8rem 0 5rem;
  background-color: ${props => props.theme.colors.white};
  background-image:
    linear-gradient(rgba(29, 78, 216, 0.05) 0.0625rem, transparent 0.0625rem),
    linear-gradient(to right, rgba(29, 78, 216, 0.05) 0.0625rem, transparent 0.0625rem);
  background-size: 1.25rem 1.25rem;
  
  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    padding: 7rem 0 4rem;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 6rem 0 3rem;
  }
`;

const Container = styled.div`
  max-width: 75rem;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 10;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 0 1rem;
  }
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  
  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }
`;

const HeroContent = styled.div`
  animation: ${fadeInLeft} 0.8s ease-out;
  
  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    order: 2;
  }
`;

const HeroTitle = styled.h1`
  font-family: ${props => props.theme.fonts.heading};
  font-size: 4rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  line-height: 1.1;
  text-shadow: 0.0625rem 0.0625rem 0.125rem rgba(0, 0, 0, 0.1);
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;
  
  .text-primary {
    color: ${props => props.theme.colors.text};
  }
  
  .blue-gradient {
    background: ${props => props.theme.gradients.primary};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    font-size: 3.5rem;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 2.5rem;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
    width: calc(100% + 0.1875rem);
    letter-spacing: -0.01em;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.25rem;
  color: ${props => props.theme.colors.textLight};
  margin-bottom: 2rem;
  line-height: 1.6;
  animation: ${fadeInUp} 0.8s ease-out 0.4s both;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 1.1rem;
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1rem;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  animation: ${fadeInUp} 0.8s ease-out 0.6s both;
  
  @media (min-width: ${props => props.theme.breakpoints.tablet}) {
    flex-direction: row;
    justify-content: center;
    gap: 1.5rem;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    justify-content: center;
  }
  
  @media (min-width: ${props => props.theme.breakpoints.desktopUp}) {
    justify-content: flex-start;
  }
`;

const Button = styled.button`
  padding: 1rem 2rem;
  border-radius: 0.5rem;
  font-size: 1.125rem;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 12.5rem;
  
  &:hover {
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.98);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 0.875rem 1.5rem;
    font-size: 1rem;
    min-width: auto;
    width: 100%;
  }
`;

const PrimaryButton = styled(Button)`
  background: ${props => props.theme.gradients.primary};
  color: ${props => props.theme.colors.white};
  box-shadow: ${props => props.theme.shadows.lg};
  
  &:hover {
    background: var(--gradient-blue);
    box-shadow: ${props => props.theme.shadows.xl};
  }
`;

const SecondaryButton = styled(Button)`
  background: transparent;
  color: ${props => props.theme.colors.primary};
  border: 0.125rem solid ${props => props.theme.colors.primary};
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.white};
  }
`;

const FeaturesList = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 3rem;
  animation: ${fadeInUp} 0.8s ease-out 0.8s both;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    margin-top: 2rem;
  }
`;

const FeatureItem = styled.div`
  text-align: center;
  padding: 0.5rem;
  border-radius: 0.5rem;
`;

const FeatureIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.25rem;
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: ${props => props.theme.colors.primary};
    margin-right: 0.5rem;
  }
`;

const FeatureText = styled.span`
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  font-size: 0.875rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 0.75rem;
  }
`;

const HeroImageContainer = styled.div`
  position: relative;
  animation: ${fadeInRight} 0.8s ease-out 0.4s both;
  
  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    order: 1;
  }
`;

const HeroImage = styled.div`
  position: relative;
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.xl};
  
  img {
    width: 100%;
    height: auto;
    max-height: 31.25rem;
    object-fit: cover;
    display: block;
  }
  
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(255, 255, 255, 0.2) 0%, transparent 100%);
    pointer-events: none;
  }
`;

export const Hero: React.FC = () => {
  const handleQuoteClick = () => {
    // Placeholder for quote functionality
    console.log('Quote button clicked');
  };

  const handleProjectsClick = () => {
    // Scroll to gallery section
    const galleryElement = document.getElementById('galeria');
    if (galleryElement) {
      galleryElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <HeroSection id="inicio">
      <Container>
        <HeroGrid>
          <HeroContent>
            <HeroTitle>
              <span className="text-primary">Vidrería la Cañadita.</span>{' '}
              <span className="blue-gradient">Vidrios y Aluminios</span>
            </HeroTitle>
            
            <HeroDescription>
              Puertas, Ventanas, Cancelería de baños, Barandales, Espejos, Vidrios de diferentes espesor. Trabajos garantizados con calidad y elegancia en cada proyecto.
            </HeroDescription>

            <ButtonContainer>
              <PrimaryButton onClick={handleQuoteClick}>
                Cotiza tu Proyecto
              </PrimaryButton>
              <SecondaryButton onClick={handleProjectsClick}>
                Ver Proyectos
              </SecondaryButton>
            </ButtonContainer>

            <FeaturesList>
              <FeatureItem>
                <FeatureIcon>
                  <Icon name="Check" size={18} />
                  <FeatureText>Calidad</FeatureText>
                </FeatureIcon>
              </FeatureItem>
              <FeatureItem>
                <FeatureIcon>
                  <Icon name="ShieldCheck" size={18} />
                  <FeatureText>Trabajos Garantizados</FeatureText>
                </FeatureIcon>
              </FeatureItem>
              <FeatureItem>
                <FeatureIcon>
                  <Icon name="Truck" size={18} />
                  <FeatureText>Instalación</FeatureText>
                </FeatureIcon>
              </FeatureItem>
            </FeaturesList>
          </HeroContent>

          <HeroImageContainer>
            <HeroImage>
              <img 
                src="/images/puerta-22.webp" 
                alt="Moderna sala con grandes ventanales de aluminio"
              />
            </HeroImage>
          </HeroImageContainer>
        </HeroGrid>
      </Container>
    </HeroSection>
  );
};
